import { useCallback, useEffect, useRef, useState } from 'react';
import { API_URL } from '../config/api';
import { computeKasaOzeti, formatKasaAmount, formatKasaSummaryValue } from '../utils/kasaOzeti';
import './MobileKasaOzeti.css';

// iOS admin uygulamasının WebView'da açtığı rapor sayfaları için ortak altyapı:
//   /mobile/kasa-ozeti?store_id=<id>&date=YYYY-MM-DD  (bu dosya)
//   /mobile/gun-sonu?store_id=<id>&date=YYYY-MM-DD    (MobileGunSonu.js)
// index.js bu yolları App olmadan (login, mağaza seçimi, hatırlatmalar yok) doğrudan render eder.
// Oturum token'ını iOS uygulaması sayfaya aktarır (window.__akikAuthToken → utils/apiAuth.js); şifre sorulmaz.
// iOS tarih/mağaza değişiminde sayfayı yeniden yüklemek yerine window.akikMobileReport.load(storeId, date) çağırır.

const readParams = () => {
  const params = new URLSearchParams(window.location.search);
  return { storeId: params.get('store_id'), date: params.get('date') };
};

// YYYY-MM-DD → yerel öğlen: saat dilimi farkı tarihi bir gün kaydırmasın
const formatReportDate = (date) => {
  const d = new Date(`${date}T12:00:00`);
  if (Number.isNaN(d.getTime())) return date;
  return `${d.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' })}, ${d.toLocaleDateString('tr-TR', { weekday: 'long' })}`;
};

// /api/daily-report mağaza adını döndürmüyor (web'deki Gün Sonu da adı seçili mağazadan ekliyor);
// ad mağaza listesinden bulunur. Liste bir kez çekilir, başarısız olursa sonraki yüklemede tekrar denenir.
let storesPromise = null;
const fetchStoreName = async (storeId) => {
  if (!storesPromise) {
    storesPromise = fetch(`${API_URL}/stores`).then(r => (r.ok ? r.json() : [])).catch(() => []);
  }
  const stores = await storesPromise;
  if (!Array.isArray(stores) || stores.length === 0) {
    storesPromise = null;
    return null;
  }
  return stores.find(s => String(s.id) === String(storeId))?.name || null;
};

export const useMobileDailyReport = ({ title, forceLight = false }) => {
  const [report, setReport] = useState(null); // { data, date }
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [error, setError] = useState('');
  const requestIdRef = useRef(0);

  const load = useCallback(async (storeId, date) => {
    const requestId = ++requestIdRef.current;
    setStatus('loading');
    try {
      if (!storeId || !date) throw new Error('Mağaza veya tarih bilgisi eksik');
      const [response, storeName] = await Promise.all([
        fetch(`${API_URL}/daily-report?store_id=${encodeURIComponent(storeId)}&date=${encodeURIComponent(date)}`, { cache: 'no-store' }),
        fetchStoreName(storeId),
      ]);
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error || `Sunucu hatası (${response.status})`);
      if (requestId !== requestIdRef.current) return false;
      setReport({ data: { ...data, storeName: storeName || data.storeName || 'Mağaza' }, date });
      setError('');
      setStatus('ready');
      return true;
    } catch (e) {
      if (requestId !== requestIdRef.current) return false;
      setReport(null);
      setError(e.message || 'Bağlantı hatası');
      setStatus('error');
      return false;
    }
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.add('mko-html');
    if (forceLight) root.classList.add('mko-force-light');
    document.title = title;
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) viewport.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no');
    return () => root.classList.remove('mko-html', 'mko-force-light');
  }, [title, forceLight]);

  useEffect(() => {
    const { storeId, date } = readParams();
    load(storeId, date);

    window.akikMobileReport = {
      load: (nextStoreId, nextDate) => {
        const query = `?store_id=${encodeURIComponent(nextStoreId)}&date=${encodeURIComponent(nextDate)}`;
        window.history.replaceState(null, '', `${window.location.pathname}${query}`);
        window.scrollTo(0, 0);
        return load(String(nextStoreId), String(nextDate));
      },
    };
    return () => { delete window.akikMobileReport; };
  }, [load]);

  const retry = useCallback(() => {
    const { storeId, date } = readParams();
    load(storeId, date);
  }, [load]);

  return { report, status, error, retry };
};

// İlk yükleme göstergesi ve hata ekranı (yenileme sırasında mevcut rapor soluk gösterilir)
export const MobileReportStatus = ({ status, error, hasReport, onRetry }) => {
  if (status === 'error') {
    return (
      <div className="mko-state">
        <strong className="mko-state-title">Rapor yüklenemedi</strong>
        <span className="mko-state-detail">{error}</span>
        <button type="button" className="mko-retry" onClick={onRetry}>Tekrar Dene</button>
      </div>
    );
  }
  if (status === 'loading' && !hasReport) {
    return (
      <div className="mko-state">
        <div className="mko-spinner" />
        <span>Rapor yükleniyor…</span>
      </div>
    );
  }
  return null;
};

export const KasaOzetiPaper = ({ report, refreshing }) => {
  const kasa = computeKasaOzeti(report.data);
  return (
    <article className={`mko-paper${refreshing ? ' is-refreshing' : ''}`}>
      <header className="mko-header">
        <div className="mko-title">KASA DEFTERİ RAPORU DETAYLI</div>
        <div className="mko-meta">
          <span className="mko-meta-label">Tarih</span>
          <span className="mko-meta-value">{formatReportDate(report.date)}</span>
        </div>
      </header>

      <div className="mko-store">{report.data.storeName || 'Merkez'}</div>

      <section className="mko-section">
        <div className="mko-section-title gelir">(+) GELİRLER</div>
        <div className="mko-table-head"><span>İŞLEM ADI</span><span>TUTAR</span></div>
        {kasa.gelirItems.length > 0 ? kasa.gelirItems.map(item => (
          <div key={item.label} className="mko-row">
            <span className="mko-label">{item.label}</span>
            <span className="mko-amount">{formatKasaAmount(item.amount)}</span>
          </div>
        )) : (
          <div className="mko-row is-empty"><span className="mko-label">Gelir bulunmuyor</span><span className="mko-amount">0,00</span></div>
        )}
        <div className="mko-section-total">
          <span>TOPLAM :</span>
          <span className="mko-amount">{formatKasaAmount(kasa.gelirTop)} (+)</span>
        </div>
      </section>

      <section className="mko-section">
        <div className="mko-section-title gider">(-) GİDERLER</div>
        <div className="mko-table-head"><span>İŞLEM ADI</span><span>TUTAR</span></div>
        {kasa.giderItems.length > 0 ? kasa.giderItems.map(item => (
          <div key={item.label} className="mko-row is-expense">
            <span className="mko-label">{item.label}</span>
            <span className="mko-amount">-{formatKasaAmount(item.amount)}</span>
          </div>
        )) : (
          <div className="mko-row is-empty"><span className="mko-label">Gider bulunmuyor</span><span className="mko-amount">0,00</span></div>
        )}
        <div className="mko-section-total">
          <span>TOPLAM :</span>
          <span className="mko-amount">{formatKasaAmount(kasa.giderTop)} (-)</span>
        </div>
      </section>

      <div className="mko-genel">
        <span className="mko-genel-label">KASA GENEL TOPLAM</span>
        <span className="mko-genel-value">{formatKasaAmount(kasa.kasaGenel)} {kasa.kasaGenel >= 0 ? '(+)' : '(-)'}</span>
      </div>

      {kasa.summaryRows.length > 0 && (
        <div className="mko-summary">
          {kasa.summaryRows.map(row => (
            <div key={row.label} className="mko-summary-row">
              <span className="mko-label">{row.label}</span>
              <span className="mko-amount">{formatKasaSummaryValue(row)}</span>
            </div>
          ))}
        </div>
      )}
    </article>
  );
};

function MobileKasaOzeti() {
  const { report, status, error, retry } = useMobileDailyReport({ title: 'Kasa Özeti' });
  return (
    <div className="mko-page" aria-busy={status === 'loading'}>
      <MobileReportStatus status={status} error={error} hasReport={!!report} onRetry={retry} />
      {report && <KasaOzetiPaper report={report} refreshing={status === 'loading'} />}
    </div>
  );
}

export default MobileKasaOzeti;
